/**
 * Canonical Store marketplace categories.
 * Single source of truth for browsing labels and persisted category values.
 */
var STORE_CATEGORIES = Object.freeze({
  APPS_AND_GAMES: 'Apps & Games',
  EXTENSIONS: 'Extensions',
  THEMES: 'Themes',
  IMAGE_NFTS: 'Image NFTs',
  TOKENS: 'Tokens',
  ACCESS_KEYS: 'Access Keys',
  MERCHANDISE: 'Merchandise',
  OTHER: 'Other'
});

/** Ordered list for Store navigation / browsing UI. */
var STORE_CATEGORY_LIST = Object.freeze([STORE_CATEGORIES.APPS_AND_GAMES, STORE_CATEGORIES.EXTENSIONS, STORE_CATEGORIES.THEMES, STORE_CATEGORIES.IMAGE_NFTS, STORE_CATEGORIES.TOKENS, STORE_CATEGORIES.ACCESS_KEYS, STORE_CATEGORIES.MERCHANDISE, STORE_CATEGORIES.OTHER]);
var DEFAULT_PAGE_SIZE = 24;
var MAX_PAGE_SIZE = 48;
function categoryViewKey() {
  var category = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return String(category).toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
function isStoreCategory() {
  var category = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return STORE_CATEGORY_LIST.includes(String(category || ''));
}

/**
 * Buyer-facing Type label: a STORE_CATEGORY_LIST value, or Other.
 * Uses a stored listing category when it is one of the canonical labels;
 * otherwise maps protocol nft_type (unknown → Other).
 */
function storeCategoryLabel() {
  var category = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var nft_type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var stored = String(category || '').trim();
  if (isStoreCategory(stored)) {
    return stored;
  }
  return mapNFTTypeToCategory(nft_type);
}
function normalizePageSize(page_size) {
  var n = Number(page_size);
  if (!Number.isFinite(n) || n <= 0) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(MAX_PAGE_SIZE, Math.max(1, Math.floor(n)));
}
function normalizePage(page) {
  var n = Number(page);
  if (!Number.isFinite(n) || n <= 0) {
    return 1;
  }
  return Math.floor(n);
}
function normalizeOffset(offset) {
  var n = Number(offset);
  if (!Number.isFinite(n) || n < 0) {
    return 0;
  }
  return Math.floor(n);
}

/**
 * Protocol / module NFT type → Store category.
 * Unknown and empty types resolve to Other.
 *
 * @param {string} nft_type
 * @returns {string} one of STORE_CATEGORIES values
 */
function mapNFTTypeToCategory() {
  var nft_type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var type = String(nft_type || '').trim();
  switch (type) {
    case 'image':
      return STORE_CATEGORIES.IMAGE_NFTS;
    case 'token':
      return STORE_CATEGORIES.TOKENS;
    case 'css':
      return STORE_CATEGORIES.THEMES;
    case 'js':
      return STORE_CATEGORIES.EXTENSIONS;
    case 'stack':
    case 'nwasm-nft-mod':
    case 'saito-app':
      return STORE_CATEGORIES.APPS_AND_GAMES;
    case 'vault-nft-key':
    case 'vault-nft-rental':
    case 'store-nft-rental':
      return STORE_CATEGORIES.ACCESS_KEYS;
    case 'text':
    case 'json':
    default:
      return STORE_CATEGORIES.OTHER;
  }
}

/**
 * Vault rental NFT that the Store RENT picker lists as rental source inventory.
 * Exact type only — not vault-nft-key / vault masters, not store-nft-rental.
 */
function isVaultRentalNftType() {
  var nft_type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return String(nft_type || '').trim().toLowerCase() === 'vault-nft-rental';
}

/**
 * NFTs eligible for the Store SELL picker.
 * Vault rental NFTs are RENT-only; Store disposable rentals are not ordinary sell stock.
 */
function isSellableNftType() {
  var nft_type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var type = String(nft_type || '').trim().toLowerCase();
  if (type === 'vault-nft-rental') {
    return false;
  }
  if (type === 'store-nft-rental') {
    return false;
  }
  return true;
}

/**
 * Buyer-facing rental listing detection from summary + listing txmsg.listing.
 */
function isStoreRentalListing() {
  var _summary$nft, _summary$nft2;
  var summary = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var listing_meta = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (String((listing_meta === null || listing_meta === void 0 ? void 0 : listing_meta.listing_mode) || '').toLowerCase() === 'rent') {
    return true;
  }
  var nft_type = (typeof (summary === null || summary === void 0 || (_summary$nft = summary.nft) === null || _summary$nft === void 0 ? void 0 : _summary$nft.returnType) === 'function' ? summary.nft.returnType() : '') || (summary === null || summary === void 0 || (_summary$nft2 = summary.nft) === null || _summary$nft2 === void 0 ? void 0 : _summary$nft2.nft_type) || (summary === null || summary === void 0 ? void 0 : summary.productType) || '';
  return String(nft_type).trim().toLowerCase() === 'store-nft-rental';
}
function normalizeListingMode() {
  var mode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'sell';
  return String(mode || '').toLowerCase() === 'rent' ? 'rent' : 'sell';
}
module.exports = {
  STORE_CATEGORIES: STORE_CATEGORIES,
  STORE_CATEGORY_LIST: STORE_CATEGORY_LIST,
  DEFAULT_PAGE_SIZE: DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE: MAX_PAGE_SIZE,
  categoryViewKey: categoryViewKey,
  isStoreCategory: isStoreCategory,
  storeCategoryLabel: storeCategoryLabel,
  normalizePageSize: normalizePageSize,
  normalizePage: normalizePage,
  normalizeOffset: normalizeOffset,
  mapNFTTypeToCategory: mapNFTTypeToCategory,
  isVaultRentalNftType: isVaultRentalNftType,
  isSellableNftType: isSellableNftType,
  isStoreRentalListing: isStoreRentalListing,
  normalizeListingMode: normalizeListingMode
};